import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ToggleButtonGroup, ToggleOption } from "../../../components/toggle-button-group";
import { FileInput } from "../../../components/file-input";
import { AvatarModal } from "../../../settings/profile-picture/components/avatar-modal/avatar-modal";
import { useUpdateUserPhotoMutation, useSetAlphabeticalUserPhotoMutation } from "../../../../../service/user-service";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { HumanAvatar } from "../../../../../assets/icons";
import styles from "./multi-step-form.module.scss";

interface Props {
	onSkip: () => void;
}

export const Step5ProfilePicture: React.FC<Props> = ({ onSkip }) => {
	const { t } = useTranslation();
	const [showOptions, setShowOptions] = useState(false);
	const [selected, setSelected] = useState<string>();
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [showAvatarModal, setShowAvatarModal] = useState(false);

	const [updateUserPhoto, { isLoading: isUploadingPhoto }] = useUpdateUserPhotoMutation();
	const [setAlphabetical, { isLoading: isSettingAlphabet }] = useSetAlphabeticalUserPhotoMutation();

	const isSaving = isUploadingPhoto || isSettingAlphabet;
	const isContinueEnabled = (selected === "image" && imageFile !== null) || selected === "alphabet";

	const handleToggleChange = (value: string) => {
		setSelected(value);
		if (value === "avatar") setShowAvatarModal(true);
	};

	const handleSave = async () => {
		if (selected === "image" && imageFile) {
			const formData = new FormData();
			formData.append("Image", imageFile);
			updateUserPhoto(formData)
				.unwrap()
				.then(() => {
					toast.success(t("auth.web.profileImageUpdated"));
					onSkip();
				})
				.catch(err => toast.error(getErrorMessage(err?.data)));
		} else if (selected === "alphabet") {
			setAlphabetical()
				.unwrap()
				.then(() => {
					toast.success(t("auth.web.profileImageUpdated"));
					onSkip();
				})
				.catch(err => toast.error(getErrorMessage(err?.data)));
		}
	};

	const profilePictureOptions: ToggleOption[] = [
		{
			value: "image",
			label: t("account.image"),
			expandOnSelect: true,
			customContent: <FileInput value={imageFile} onChange={setImageFile} accept="image/*" placeholder={t("auth.web.noImageSelected")} />,
		},
		{ value: "avatar", label: t("account.avatar") },
		{ value: "alphabet", label: t("account.alphabet") },
	];

	return (
		<div className={styles.stepContent}>
			{!showOptions ? (
				<div className={styles.avatarPicker}>
					<button type="button" className={styles.avatarTapButton} onClick={() => setShowOptions(true)}>
						<HumanAvatar />
					</button>
					<span className={styles.avatarTapLabel}>{t("common.labels.tapToChange")}</span>
				</div>
			) : (
				<div className={styles.card}>
					<ToggleButtonGroup
						options={profilePictureOptions}
						value={selected}
						onChange={handleToggleChange}
						name="profile-picture-type"
						direction="vertical"
						variant="mobile"
					/>
				</div>
			)}

			<div className={styles.buttonSection}>
				{isContinueEnabled && (
					<button type="button" className={styles.continueButton} onClick={handleSave} disabled={isSaving}>
						{isSaving ? t("auth.web.saving") : t("common.actions.continueKey")}
					</button>
				)}
				<button type="button" className={styles.skipButton} onClick={onSkip}>
					{t("auth.skipForNow")}
				</button>
			</div>

			<AvatarModal
				isOpen={showAvatarModal}
				onClose={() => {
					setShowAvatarModal(false);
					setSelected(undefined);
				}}
				onSave={() => {
					setShowAvatarModal(false);
					onSkip();
				}}
			/>
		</div>
	);
};
